import type { User } from 'firebase/auth';
import { MEDIA_GATEWAY, auth } from './firebase';
import { retryBrokenMedia } from './mediaRetry';

/**
 * The media gateway cannot read an Authorization header off an <img> tag, so
 * it issues a cookie instead. This trades the ID token for that cookie, and
 * the cookie is what every subsequent photo and video request rides on.
 *
 * Media URLs therefore stay stable and the service worker can cache them
 * forever, which is the whole reason for doing it this way rather than signing
 * each URL.
 */

/*
 * When the cookie runs out, and whose it is -- kept on the phone, not only in
 * memory, because the cookie outlives the page by hours: forgetting it meant
 * every cold opening asked the gateway for a session it already had, and the
 * app waited for that round trip before showing anything.
 */
const KEPT_KEY = 'givisnap:mediaSession';
let expiresAt = 0;
let holder = '';
let inFlight: Promise<void> | null = null;

/** Long enough for a slow line, short enough that the app never hangs on it. */
const SESSION_TIMEOUT_MS = 8_000;

function readKept(): void {
  try {
    const [uid, until] = (localStorage.getItem(KEPT_KEY) ?? '').split(' ');
    if (uid && Number(until) > expiresAt) {
      holder = uid;
      expiresAt = Number(until);
    }
  } catch {
    // Blocked storage: memory only, as before.
  }
}

export function sessionIsFresh(uid?: string): boolean {
  readKept();
  // Somebody else's on a shared phone is no session for this person.
  if (uid && holder && holder !== uid) return false;
  // Refresh a few minutes early so a long video does not start on a cookie
  // that expires mid-stream.
  return Date.now() < expiresAt - 5 * 60_000;
}

export async function openMediaSession(user: User, force = false): Promise<void> {
  if (!force && sessionIsFresh(user.uid)) return;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const idToken = await user.getIdToken();
    const res = await fetch(`${MEDIA_GATEWAY}/session`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken }),
      // Not on iOS before 16, which simply waits as it always did.
      signal: typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(SESSION_TIMEOUT_MS) : undefined,
    });

    if (res.status === 403) throw new NotAMemberError();
    if (!res.ok) throw new Error(`media session failed: ${res.status}`);

    const body = (await res.json()) as { expiresAt: number };
    expiresAt = body.expiresAt;
    holder = user.uid;
    // Whatever was refused while there was no session can be had now.
    retryBrokenMedia();
    try {
      localStorage.setItem(KEPT_KEY, `${user.uid} ${expiresAt}`);
    } catch {
      // Memory only: asked for again on the next cold opening.
    }
  })().finally(() => {
    inFlight = null;
  });

  return inFlight;
}

let lastRenewal = 0;
/** However many pictures fail at once, they share one new cookie. */
const RENEW_EVERY_MS = 30_000;

/**
 * A fresh cookie after a picture fails to load. A screen of thumbnails that
 * all failed together asks once, not once each, and a gateway that is simply
 * down is not asked again for half a minute.
 */
export async function renewMediaSession(): Promise<void> {
  const user = auth.currentUser;
  if (!user) return;
  // Stamped before asking, so the rest of a screen failing at once waits too.
  if (Date.now() - lastRenewal < RENEW_EVERY_MS) return;
  lastRenewal = Date.now();
  return openMediaSession(user, true);
}

/**
 * A new cookie on coming back to the app, if the old one has run out.
 *
 * The session is opened when the app starts and lasts twelve hours, but a
 * phone keeps the page loaded in the background for far longer than that. The
 * GiviSnap notification is what usually brings it back, so the pictures that
 * are then unfrosted were all being asked for on a dead cookie.
 */
export function keepMediaSessionFresh(root: Document = document): () => void {
  const back = () => {
    if (root.visibilityState !== 'visible') return;
    const user = auth.currentUser;
    // Unforced, so a session with time left on it is not asked for again.
    if (user) void openMediaSession(user).catch(() => undefined);
  };
  root.addEventListener('visibilitychange', back);
  return () => root.removeEventListener('visibilitychange', back);
}

export async function closeMediaSession(): Promise<void> {
  expiresAt = 0;
  holder = '';
  try {
    localStorage.removeItem(KEPT_KEY);
  } catch {
    // Nothing kept to forget.
  }
  await fetch(`${MEDIA_GATEWAY}/session`, {
    method: 'DELETE',
    credentials: 'include',
  }).catch(() => undefined);
}

export class NotAMemberError extends Error {
  constructor() {
    super('not a family member');
  }
}
